import React, { useEffect, useRef, useState } from 'react';
import { apiUrl } from '../config/api';
import { getStoredUser } from '../utils/auth';
import { useFormSubmit } from '../context/FormSubmitContext';

const fieldClass =
  'h-7 rounded border border-gray-300 bg-white px-2 text-[11px] font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400';

function formatDisplayDateTime(date = new Date()) {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toLocalDateTimeValue(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Location/Sites dropdown + read-only current date/time for the fixed safety-form heading bar.
 * Must be rendered inside FormSubmitProvider.
 */
export function FormSiteTimestampControls({ className = '' }) {
  const {
    selectedSiteId,
    setSelectedSiteId,
    setSelectedSiteName,
    setSelectedSiteCompanyName,
    setSelectedSiteCompanyLogoUrl,
    formTimestamp,
    setFormTimestamp,
    setMessage,
    message,
    formImages,
    addFormImages,
    removeFormImage,
  } = useFormSubmit();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayDateTime] = useState(() => formatDisplayDateTime());
  const [showAllImages, setShowAllImages] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const galleryInputRef = useRef(null);
  const uploadMenuRef = useRef(null);
  const fileInputRef = galleryInputRef;

  useEffect(() => {
    if (!formTimestamp) {
      setFormTimestamp(toLocalDateTimeValue());
    }
  }, [formTimestamp, setFormTimestamp]);

  useEffect(() => {
    let cancelled = false;
    const user = getStoredUser();
    if (!user?.email) {
      setLoading(false);
      return undefined;
    }

    (async () => {
      try {
        const res = await fetch(apiUrl('/api/admin/sites'), {
          headers: { 'X-User-Email': user.email },
        });
        if (!res.ok) throw new Error('Failed to load sites');
        const data = await res.json();
        if (!cancelled) setSites(Array.isArray(data.sites) ? data.sites : []);
      } catch {
        if (!cancelled) setSites([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const applySiteSelection = (id) => {
    setSelectedSiteId(id);
    const match = sites.find((s) => String(s.id) === String(id));
    setSelectedSiteName(match?.name || '');
    setSelectedSiteCompanyName(match?.company_name || '');
    const logoPath = match?.company_logo_url || '';
    setSelectedSiteCompanyLogoUrl(logoPath ? apiUrl(logoPath) : '');
  };

  const handleSiteChange = (e) => {
    applySiteSelection(e.target.value);
    setMessage(null);
  };

  const handleImagePick = (e) => {
    addFormImages?.(e.target.files);
    e.target.value = '';
    setShowUploadMenu(false);
  };

  const openGallery = () => {
    galleryInputRef.current?.click();
    setShowUploadMenu(false);
  };

  const openCamera = () => {
    setShowUploadMenu(false);
    setShowCamera(true);
  };

  const handleCameraCapture = (fileOrFiles) => {
    const files = Array.isArray(fileOrFiles)
      ? fileOrFiles
      : fileOrFiles
        ? [fileOrFiles]
        : [];
    if (files.length) addFormImages?.(files);
    setShowCamera(false);
  };

  useEffect(() => {
    if (!showUploadMenu) return undefined;
    const handlePointerDown = (event) => {
      if (!uploadMenuRef.current?.contains(event.target)) {
        setShowUploadMenu(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [showUploadMenu]);

  const siteRequiredError =
    message?.type === 'error' &&
    message?.text === 'Please select a Location / Site before submitting.';

  const imageList = Array.isArray(formImages) ? formImages : [];
  const visibleImages = imageList.slice(0, 3);
  const extraImageCount = Math.max(0, imageList.length - 3);

  return (
    <div
      className={`flex w-full min-w-0 flex-col items-stretch gap-1 md:ml-auto md:w-auto md:shrink-0 md:items-end ${className}`}
      aria-label="Form location and timestamp"
    >
      <div className="flex w-full min-w-0 flex-col gap-1.5 md:w-auto md:flex-row md:flex-wrap md:items-center md:justify-end">
        {/* Gallery picker: multiple images from device photos */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleImagePick}
        />
        <div className="relative" ref={uploadMenuRef}>
          <button
            type="button"
            onClick={() => setShowUploadMenu((open) => !open)}
            className="inline-flex h-8 w-full items-center justify-center gap-1 rounded border border-gray-300 bg-white px-2 text-[11px] font-semibold text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-400 md:h-7 md:w-auto md:justify-start"
            title="Upload one or more images"
            aria-expanded={showUploadMenu}
            aria-haspopup="menu"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5 shrink-0 text-gray-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 16V5" />
              <path d="M8 9l4-4 4 4" />
              <path d="M4 18h16" />
            </svg>
            Upload image
            {imageList.length ? (
              <span className="inline-flex min-w-[0.95rem] items-center justify-center rounded-full bg-gray-800 px-1 text-[9px] leading-none text-white">
                {imageList.length}
              </span>
            ) : null}
          </button>
          {showUploadMenu ? (
            <div
              className="absolute left-0 top-full z-[90] mt-1 w-44 overflow-hidden rounded-md border border-gray-300 bg-white py-1 shadow-lg md:left-auto md:right-0"
              role="menu"
            >
              <button
                type="button"
                role="menuitem"
                onClick={openGallery}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-semibold text-gray-800 hover:bg-gray-50"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-3.5 w-3.5 shrink-0 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" />
                  <path d="M21 16l-5-4-7 6" />
                </svg>
                Choose from gallery
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={openCamera}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-semibold text-gray-800 hover:bg-gray-50"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-3.5 w-3.5 shrink-0 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 8h3l1.5-2h7L17 8h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2z" />
                  <circle cx="12" cy="14" r="3.2" />
                </svg>
                Take picture
              </button>
            </div>
          ) : null}
        </div>

        <label className="sr-only" htmlFor="form-location-site">
          Location / Sites
        </label>
        <select
          id="form-location-site"
          value={selectedSiteId || ''}
          onChange={handleSiteChange}
          disabled={loading}
          required
          aria-required="true"
          aria-invalid={siteRequiredError || undefined}
          className={`${fieldClass} w-full min-w-0 md:w-auto md:min-w-[8.5rem] md:max-w-[11.5rem] ${
            siteRequiredError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''
          }`}
          title="Location / Sites"
        >
          <option value="">{loading ? 'Loading sites…' : 'Location / Sites *'}</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </select>

        <time
          dateTime={formTimestamp || undefined}
          className="inline-flex h-8 w-full items-center justify-center rounded border border-gray-200 bg-gray-50 px-2 text-[11px] font-semibold text-gray-800 tabular-nums whitespace-nowrap md:h-7 md:w-auto md:min-w-[9.5rem] md:justify-start"
          title="Current date and time"
        >
          {displayDateTime}
        </time>
      </div>

      {imageList.length ? (
        <div className="w-full space-y-0.5 text-left md:max-w-[16rem] md:text-right">
          {visibleImages.map((img) => (
            <div key={img.id} className="flex items-center justify-between gap-1 md:justify-end">
              <span
                className="min-w-0 truncate text-[10px] font-semibold text-gray-700"
                title={img.name}
              >
                {img.name}
              </span>
              <button
                type="button"
                onClick={() => removeFormImage?.(img.id)}
                className="shrink-0 text-[11px] font-semibold leading-none text-red-600 hover:text-red-700"
                aria-label={`Remove ${img.name}`}
              >
                ×
              </button>
            </div>
          ))}
          {extraImageCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowAllImages(true)}
              className="text-[10px] font-semibold text-amber-700 hover:text-amber-800 underline"
            >
              See more ({extraImageCount})
            </button>
          ) : null}
        </div>
      ) : null}

      {showAllImages ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3"
          onClick={() => setShowAllImages(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-lg border border-gray-300 bg-white p-3 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Uploaded images"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Uploaded images ({imageList.length})
              </h4>
              <button
                type="button"
                onClick={() => setShowAllImages(false)}
                className="text-xs font-semibold text-gray-500 hover:text-gray-800"
              >
                Close
              </button>
            </div>
            <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {imageList.length ? (
                imageList.map((img) => (
                  <li
                    key={img.id}
                    className="flex items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1.5"
                  >
                    <span
                      className="min-w-0 truncate text-xs font-semibold text-gray-800"
                      title={img.name}
                    >
                      {img.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        removeFormImage?.(img.id);
                        if (imageList.length <= 1) setShowAllImages(false);
                      }}
                      className="shrink-0 text-sm font-semibold leading-none text-red-600 hover:text-red-700"
                      aria-label={`Remove ${img.name}`}
                    >
                      ×
                    </button>
                  </li>
                ))
              ) : (
                <li className="text-xs text-gray-500 py-2">No images uploaded.</li>
              )}
            </ul>
          </div>
        </div>
      ) : null}
      {showCamera ? (
        <CameraCaptureModal
          multiple
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      ) : null}
    </div>
  );
}

function stopMediaStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

export function CameraCaptureModal({ onCapture, onClose, multiple = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraFileRef = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [captured, setCaptured] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setError('');

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera is not supported in this browser.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stopMediaStream(stream);
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {});
          if (!cancelled) setReady(true);
        }
      } catch (err) {
        if (cancelled) return;
        const name = err?.name || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setError('Camera permission was blocked. Allow camera access and try again.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setError('No camera was found on this device.');
        } else {
          setError('Could not open the camera. You can still use the device camera app.');
        }
      }
    })();

    return () => {
      cancelled = true;
      stopMediaStream(streamRef.current);
      streamRef.current = null;
    };
  }, [facingMode]);

  const finishWithFiles = (files) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : files ? [files] : [];
    if (!list.length) return;
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    onCapture(multiple || list.length > 1 ? list : list[0]);
  };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        if (multiple) {
          setCaptured((prev) => [...prev, file]);
          return;
        }
        finishWithFiles([file]);
      },
      'image/jpeg',
      0.92
    );
  };

  const handleFallbackFile = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (!picked.length) return;
    if (multiple) {
      setCaptured((prev) => [...prev, ...picked]);
      return;
    }
    finishWithFiles(picked);
  };

  const handleDone = () => {
    if (!captured.length) return;
    finishWithFiles(captured);
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/70 p-2 sm:items-center sm:p-3"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md max-h-[calc(100dvh-0.5rem)] overflow-y-auto overflow-x-hidden rounded-t-2xl border border-gray-300 bg-white shadow-xl sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Take picture"
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
          <h4 className="text-sm font-semibold text-gray-900">
            {multiple ? 'Take pictures' : 'Take picture'}
            {multiple && captured.length ? (
              <span className="ml-2 text-xs font-semibold text-gray-500">({captured.length})</span>
            ) : null}
          </h4>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center px-1 text-xs font-semibold text-gray-500 hover:text-gray-800 sm:min-h-0"
          >
            Close
          </button>
        </div>
        <div className="bg-black">
          <video
            ref={videoRef}
            className="mx-auto block h-[min(42dvh,16rem)] w-full object-cover sm:h-[min(50vh,20rem)]"
            playsInline
            muted
            autoPlay
          />
        </div>
        {error ? (
          <p className="px-3 pt-2 text-[11px] font-semibold text-red-600">{error}</p>
        ) : null}
        {multiple && captured.length ? (
          <p className="px-3 pt-2 text-[11px] font-semibold text-gray-600">
            {captured.length} photo{captured.length === 1 ? '' : 's'} ready — capture more or tap Done
          </p>
        ) : null}
        <div className="flex flex-col gap-2 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() =>
              setFacingMode((mode) => (mode === 'environment' ? 'user' : 'environment'))
            }
            className="h-10 w-full rounded border border-gray-300 px-2 text-[11px] font-semibold text-gray-800 hover:bg-gray-50 sm:h-8 sm:w-auto"
          >
            Flip camera
          </button>
          <div className="flex items-center gap-2">
            <input
              ref={cameraFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple={multiple || undefined}
              className="sr-only"
              onChange={handleFallbackFile}
            />
            <button
              type="button"
              onClick={() => cameraFileRef.current?.click()}
              className="h-10 flex-1 rounded border border-gray-300 px-2 text-[11px] font-semibold text-gray-800 hover:bg-gray-50 sm:h-8 sm:flex-none"
            >
              Device camera
            </button>
            <button
              type="button"
              onClick={takePhoto}
              disabled={!ready}
              className="h-10 flex-1 rounded bg-[#eda448] px-3 text-[11px] font-semibold text-gray-900 hover:bg-[#d8932e] disabled:cursor-not-allowed disabled:opacity-50 sm:h-8 sm:flex-none"
            >
              Capture
            </button>
            {multiple ? (
              <button
                type="button"
                onClick={handleDone}
                disabled={!captured.length}
                className="h-10 flex-1 rounded border border-gray-800 bg-gray-900 px-3 text-[11px] font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 sm:h-8 sm:flex-none"
              >
                Done{captured.length ? ` (${captured.length})` : ''}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
function CompanyLogoBadge({ logoUrl, logoName }) {
  const [tone, setTone] = useState('unknown'); // 'light' | 'dark' | 'unknown'

  useEffect(() => {
    if (!logoUrl) {
      setTone('unknown');
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        const size = 48;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          setTone('unknown');
          return;
        }
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let total = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha < 24) continue;
          const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          total += luminance;
          count += 1;
        }
        if (!count) {
          setTone('unknown');
          return;
        }
        const avg = total / count;
        setTone(avg >= 170 ? 'light' : 'dark');
      } catch {
        // CORS/tainted canvas — assume light logo needs a dark pad.
        if (!cancelled) setTone('light');
      }
    };
    img.onerror = () => {
      if (!cancelled) setTone('unknown');
    };
    img.src = logoUrl;

    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  const padClass =
    tone === 'light'
      ? 'border border-slate-700 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900'
      : tone === 'dark'
        ? 'border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-gray-100'
        : 'border border-slate-600 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800';

  return (
    <div
      className={`mb-1.5 inline-flex items-center justify-center max-w-[11rem] p-1.5 shadow-sm ${padClass}`}
      style={{ borderRadius: '0.5rem' }}
    >
      <img
        src={logoUrl}
        alt={logoName ? `${logoName} logo` : 'Company logo'}
        className="max-h-12 max-w-full w-auto h-auto object-contain"
      />
    </div>
  );
}

/**
 * Fixed safety-form header: company logo (from selected site when chosen), heading below, sites + datetime on the right.
 */
export function SafetyFormHeader({
  title,
  companyLogoUrl = null,
  companyName = '',
  rightAside = null,
}) {
  const {
    selectedSiteId,
    selectedSiteCompanyName,
    selectedSiteCompanyLogoUrl,
  } = useFormSubmit();

  const logoUrl = selectedSiteId
    ? selectedSiteCompanyLogoUrl || companyLogoUrl
    : companyLogoUrl;
  const logoName = selectedSiteId && selectedSiteCompanyName
    ? selectedSiteCompanyName
    : companyName;

  return (
    <div className="shrink-0 border-b border-gray-300 bg-white px-3 pb-2 pt-2 md:pl-8 md:pr-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-3">
        <div className="min-w-0 md:flex-1">
          {logoUrl ? (
            <CompanyLogoBadge logoUrl={logoUrl} logoName={logoName} />
          ) : (
            <div className="mb-1.5 inline-flex h-12 max-w-[11rem] items-center justify-center rounded-lg border border-dashed border-slate-500 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800 px-3 text-[10px] text-slate-200">
              Company logo
            </div>
          )}
          <h2 className="pr-2 text-xl font-semibold leading-snug text-gray-900 md:text-2xl">{title}</h2>
        </div>
        <div className="flex w-full min-w-0 flex-col items-stretch gap-1.5 md:w-auto md:shrink-0 md:flex-row md:flex-wrap md:items-start md:justify-end">
          <FormSiteTimestampControls />
          {rightAside}
        </div>
      </div>
    </div>
  );
}
